import { getPaymentStatusBadge } from "../../utils/consts";
import { displayCurrencyAmount, displayISODate } from "../../utils/display";
import type { PaidArchiveDashboardResponse } from "../../api/queries/paymentsQueries";

type Props = {
  items: PaidArchiveDashboardResponse["items"];
  total: number;
  skip: number;
  limit: number;
  onPageChange: (skip: number) => void;
};

function formatDaysAfterDue(value: number | null): string {
  if (value == null) return "—";
  const rounded = Math.round(value);
  if (rounded > 0) return `${rounded} late`;
  if (rounded < 0) return `${Math.abs(rounded)} early`;
  return "on time";
}

export function PaidInvoicesTable({
  items,
  total,
  skip,
  limit,
  onPageChange,
}: Props) {
  const pageStart = total === 0 ? 0 : skip + 1;
  const pageEnd = Math.min(skip + limit, total);
  const canPrev = skip > 0;
  const canNext = skip + limit < total;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-gray-200 bg-slate-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 font-medium">Invoice</th>
              <th className="px-3 py-2 font-medium">Customer</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium text-right">Amount</th>
              <th className="px-3 py-2 font-medium">Invoice date</th>
              <th className="px-3 py-2 font-medium">Due date</th>
              <th className="px-3 py-2 font-medium">Paid / closed</th>
              <th className="px-3 py-2 font-medium text-right">Days after due</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-10 text-center text-gray-500"
                >
                  No settled invoices match these filters.
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const badge = getPaymentStatusBadge(item.payment_status);
                return (
                  <tr
                    key={item.id}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {item.external_id}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {item.customer_organization_name ?? "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                      {displayCurrencyAmount(item.amount, item.currency_type)}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-gray-600">
                      {displayISODate(item.date_created_in_accounting_software)}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-gray-600">
                      {displayISODate(item.due_date)}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-gray-600">
                      {item.date_paid_or_closed
                        ? displayISODate(item.date_paid_or_closed)
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                      {formatDaysAfterDue(item.days_after_due)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t border-gray-200 px-3 py-2 text-sm text-gray-600">
        <span>
          {total === 0
            ? "0 invoices"
            : `Showing ${pageStart}–${pageEnd} of ${total}`}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => onPageChange(Math.max(0, skip - limit))}
            className="rounded-md border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => onPageChange(skip + limit)}
            className="rounded-md border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
